import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/shared/auth/store';
import { useLogout } from '@/features/auth/login/useLogin';
import { AboutModal } from '@/features/about/AboutModal';
import { WorkstationPickerModal } from '@/shared/workstation/WorkstationPickerModal';
import { useWorkstationStore } from '@/shared/workstation/store';
import { ProductivityLoginModal } from '@/features/productivity/login/ProductivityLoginModal';
import { useProductivityLoginContext } from '@/features/productivity/login/useProductivityLogin';
import { PbIcon } from '@/shared/icons/PbIcon';
import { useNewScreens } from '@/shared/newscreens/useNewScreens';
import { OwnersWarehousesDialog } from '@/shared/auth/OwnersWarehousesDialog';
import { ChangeLanguageDialog } from '@/shared/translation/ChangeLanguageDialog';
import { useSearchExactMatch } from '@/shared/query/searchTextMatch';
import { useAutoHideGlobal } from '@/shared/query/autoHideDefault';
import { useGridChromeSync } from '@/shared/grid/gridChrome';
import { windowGateModeOf } from '@/shared/auth/screenAccess';
import type { AuthSession } from '@/shared/auth/types';

/**
 * Profile dropdown -- slice-316. Replaces the bare "Sign Out" button in AppHeader.
 * Shows the operator's userId + a circular avatar (initials). Click opens a menu with:
 *
 *   Session identity (ADR-0039, Accepted 2026-08-03 -- Chris's placement ruling: "for now lets
 *   stick it under the upper right user profile menu with the workstation option as well"):
 *   - Workstation:                            (opens WorkstationPickerModal; part A)
 *   - Productivity Tracking Login Change      (opens ProductivityLoginModal; part B)
 *
 *   Then:
 *   - Change password         (/profile/password   -- placeholder for now)
 *   - About PowerHouse...     (opens AboutModal)
 *   - Sign out                (logout + redirect)
 *
 * The two identity items are one visual group with one presentation: PB caption on the first line
 * (verbatim -- `dw_phfc_login.srd:16` "Workstation:", `m_main.srm:3530` "Productivity &Tracking
 * Login Change" minus the accelerator), current value on a muted second line so an operator can
 * see at a glance what this browser and this session are set to.
 *
 * The old "Productivity switcher" link to `/profile/productivity` is gone: that page is a
 * `work_mode` placeholder, a different PB concept, and ADR-0039 B3 supersedes it. The route still
 * resolves (`app/router.tsx:4927`) -- retiring the page itself is a Chris call, flagged in the
 * lane report.
 *
 * slice-3579 (BigList #19 polish, ADR-0046): kiosk sessions (`session.kiosk`) get a small
 * read-only "Kiosk" badge next to the username, and lose the three menu items that only make
 * sense for a write-capable session -- Workstation, Productivity Tracking Login Change, and
 * Change password all end in a POST that `KioskWriteBlockFilter` 403s server-side anyway, so
 * hiding them here is purely cosmetic (the filter is the real boundary). "About PowerHouse..."
 * is read-only and stays; "Sign out" stays per the same filter's explicit allow-list.
 *
 * slice-3690 (ADR-0050, DEMO ONLY): a "New Screens" slider sits above About. Chris,
 * docs/demochanges.txt 2026-09-17: "Under the user upper right corner - make a slider for 'New
 * Screens' - default it on remember it if turned off." It is a view preference, not a write, so
 * it shows for kiosk sessions too -- and it renders on the demo instance only, because that is
 * the only instance where the seven v2 screens are offered in place of the faithful ones.
 *
 * slice-3794 (ADR-0061, Chris 2026-09-28: "make a single 'Owners/Warehouses' menu under the
 * user's profile menu … allow the selection like PB does"): "Owners/Warehouses" opens
 * OwnersWarehousesDialog -- PB's File > Owner / File > Warehouse (`m_main.srm:3150-3192`,
 * w_owner_select / w_warehouse_select). Its muted second line shows the ticked sets. Hidden for
 * kiosk sessions like the other session items (the PUT is a write KioskWriteBlockFilter 403s).
 * It replaces slice-316's single-value header switcher (TenantSwitcher, removed).
 *
 * slice-4474 (John 28 Oct): "Auto-hide results after search (default)" On / Off — the default of
 * every search screen's Auto-hide tick; see AutoHideDefaultItem and shared/query/autoHideDefault.ts.
 */
export function ProfileMenu() {
  const session = useAuthStore((s) => s.session);
  const newScreens = useNewScreens();
  const navigate = useNavigate();
  const logout = useLogout();
  const [open, setOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [workstationOpen, setWorkstationOpen] = useState(false);
  const [productivityOpen, setProductivityOpen] = useState(false);
  const [scopeOpen, setScopeOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const workstation = useWorkstationStore((s) => s.current);
  const containerRef = useRef<HTMLDivElement>(null);
  // Only asked for while the menu is open (or the dialog is up), so an operator who never opens the
  // menu costs zero requests -- the summary line is a nicety, not something worth a boot-time fetch.
  const productivity = useProductivityLoginContext(open || productivityOpen);
  // slice-4474: mounted with the header, so the user's global Auto-hide default (server-side) is
  // applied to this browser at sign-in, before any search screen is opened.
  const autoHideGlobal = useAutoHideGlobal();
  // slice-4478: copy the user's Header Filters / Col Chooser grid setting into this browser.
  useGridChromeSync();

  useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    window.addEventListener('mousedown', onClickOutside);
    return () => window.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  if (!session) return null;
  const initials = (session.userId || '?').slice(0, 2).toUpperCase();
  const kiosk = session.kiosk === true;

  function goTo(path: string) {
    setOpen(false);
    navigate(path);
  }

  function handleSignOut() {
    setOpen(false);
    logout.mutate(undefined, {
      onSettled: () => navigate('/login', { replace: true }),
    });
  }

  return (
    <>
      <div ref={containerRef} className="relative">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="inline-flex items-center gap-2 rounded border border-phblue-500 px-1.5 py-0.5 text-xs text-white hover:bg-phblue-600/50"
          title="Profile"
        >
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-phblue-600 text-[10px] font-semibold">
            {initials}
          </span>
          <span className="hidden font-mono text-xs sm:inline">{session.userId}</span>
          {kiosk && <KioskBadge />}
          <svg className="h-3 w-3" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 6l4 4 4-4" strokeLinecap="round" />
          </svg>
        </button>

        {open && (
          <div className="absolute right-0 top-full z-50 mt-1 w-60 rounded-md border border-slate-200 bg-white text-slate-800 shadow-xl">
            <div className="border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500">
              Signed in as <span className="font-mono text-slate-800">{session.userId}</span>
              {kiosk && (
                <span className="ml-1">
                  <KioskBadge />
                </span>
              )}
            </div>
            {/* ADR-0039 -- one section, one presentation. Part A: device-scoped station identity
                (PB w_login's Workstation dddw). Part B: the session's productivity trio (PB
                m_file_option3 -> dw_pop_user_productivity). Both show their current value inline,
                because device- and session-scoped state that you cannot see is state you cannot
                support ("it works on my machine" becomes literally true).

                slice-3579: hidden for kiosk sessions -- each ends in a POST that
                KioskWriteBlockFilter 403s server-side, so there is nothing for the item to do. */}
            {!kiosk && (
              <>
                <MenuItem
                  label="Owners/Warehouses"
                  detail={activeScopeSummary(session)}
                  onClick={() => {
                    setOpen(false);
                    setScopeOpen(true);
                  }}
                />
                {mayChangeLanguage(session) && (
                  <MenuItem
                    // slice-4427 (ADR-0075, John 24 Oct Q2): PB File > "Change &Language"
                    // (m_phfc_main.srm:1988-2002 → w_changelang), caption minus the accelerator.
                    label="Change Language"
                    onClick={() => {
                      setOpen(false);
                      setLanguageOpen(true);
                    }}
                  />
                )}
                <MenuItem
                  label="Workstation:"
                  detail={workstation?.workstationId ?? 'Not set'}
                  onClick={() => {
                    setOpen(false);
                    setWorkstationOpen(true);
                  }}
                />
                <MenuItem
                  // m_main.srm:3530 caption verbatim, minus PB's & accelerator marker.
                  label="Productivity Tracking Login Change"
                  detail={productivitySummary(productivity.data)}
                  onClick={() => {
                    setOpen(false);
                    setProductivityOpen(true);
                  }}
                />
                <div className="my-0.5 border-t border-slate-200" />
                {/* ADR-0078 (Q153): PB Options > "Search Exact &Match" (m_phfc_main.srm:9463). */}
                <SearchExactMatchItem />
                <AutoHideDefaultItem
                  on={autoHideGlobal.on}
                  saving={autoHideGlobal.saving}
                  error={autoHideGlobal.error}
                  onChange={autoHideGlobal.setOn}
                />
                <MenuItem label="Change password" onClick={() => goTo('/profile/password')} />
              </>
            )}
            {/* slice-3690: demo instance only (ADR-0050). Kept out of the !kiosk block on
                purpose -- flipping it POSTs nothing, so KioskWriteBlockFilter has no opinion
                and a break-room display can still be switched back to the faithful screens. */}
            {newScreens.demo && (
              <>
                <NewScreensToggle
                  enabled={newScreens.enabled}
                  onChange={newScreens.setEnabled}
                />
                <div className="my-0.5 border-t border-slate-200" />
              </>
            )}
            <MenuItem
              label="About PowerHouse 10…"
              onClick={() => {
                setOpen(false);
                setAboutOpen(true);
              }}
            />
            <div className="my-0.5 border-t border-slate-200" />
            {/* slice-3565: PB's frame menu ends in File > Exit, drawn with phfc_quit
                (.\icons\phfc\quit.ico). Signing out IS that action in the web port -- it
                is the door out of the application -- so it carries the approved frame icon.
                Wired explicitly because PH10's caption ("Sign out") is not PB's ("Exit"). */}
            <MenuItem
              label={logout.isPending ? 'Signing out…' : 'Sign out'}
              pbIcon="phfc_quit"
              destructive
              disabled={logout.isPending}
              onClick={handleSignOut}
            />
          </div>
        )}
      </div>
      {aboutOpen && <AboutModal onClose={() => setAboutOpen(false)} />}
      {workstationOpen && <WorkstationPickerModal onClose={() => setWorkstationOpen(false)} />}
      {scopeOpen && <OwnersWarehousesDialog onClose={() => setScopeOpen(false)} />}
      {languageOpen && <ChangeLanguageDialog onClose={() => setLanguageOpen(false)} />}
      {productivityOpen && (
        <ProductivityLoginModal onClose={() => setProductivityOpen(false)} />
      )}
    </>
  );
}

/**
 * The muted second line under "Productivity Tracking Login Change" -- the four values PB keeps in
 * `is_productivity_*` globals (`m_main.srm:3516-3519`), joined the way an operator reads them.
 * "Not set" rather than a row of dashes, because a fresh PH10 session genuinely has no trio:
 * `LicenseRepository.insertMover` writes `warehouse_id` and leaves department / job / shift NULL.
 */
function productivitySummary(
  ctx: { warehouseId: string | null; departmentId: string | null; shiftId: string | null; jobId: string | null } | undefined,
): string {
  if (!ctx) return '…';
  const parts = [ctx.warehouseId, ctx.departmentId, ctx.shiftId, ctx.jobId].filter(
    (p): p is string => !!p && p.length > 0,
  );
  return parts.length === 0 ? 'Not set' : parts.join(' / ');
}

/**
 * slice-3794: the muted line under "Owners/Warehouses" -- the ticked warehouses, then the ticked
 * owners, as ids ("SP, DAL / PRIME"). A session persisted by a pre-3794 build has no active sets;
 * it shows the accessible lists, which is what the server treats it as.
 */
function activeScopeSummary(session: {
  warehouses?: { id: string }[];
  owners?: { id: string }[];
  activeWarehouses?: { id: string }[] | undefined;
  activeOwners?: { id: string }[] | undefined;
}): string {
  const ids = (xs: { id: string }[] | undefined) => (xs ?? []).map((x) => x.id);
  const w = ids(session.activeWarehouses ?? session.warehouses);
  const o = ids(session.activeOwners ?? session.owners);
  const part = (xs: string[]) => (xs.length === 0 ? '—' : xs.length > 3 ? `${xs.slice(0, 3).join(', ')} +${xs.length - 3}` : xs.join(', '));
  return `${part(w)} / ${part(o)}`;
}

/**
 * slice-4427: PB's window security gates File > Change Language through `w_changelang`
 * (window_modules → GENERAL-MENU / CHLANG on the client DBs). The menu item shows when that
 * window is granted, when the session predates `allowedWindows`, or while the gate only logs —
 * the same rule the router applies (ADR-0067). The server enforces it on the endpoint.
 */
function mayChangeLanguage(session: AuthSession): boolean {
  if (!Array.isArray(session.allowedWindows)) return true;
  if (windowGateModeOf(session) === 'log') return true;
  return session.allowedWindows.includes('w_changelang');
}

/**
 * slice-3579 (BigList #19 polish): read-only marker for a kiosk session (`Session.KioskUsers`,
 * ADR-0046). Cosmetic -- `KioskWriteBlockFilter` is the actual boundary -- so this never gates
 * anything by itself; it just tells the operator standing at a break-room display why the
 * Workstation / Productivity / Change-password items are gone.
 */
function KioskBadge() {
  return (
    <span
      className="rounded border border-amber-300 bg-amber-100 px-1 py-0.5 text-[9px] font-semibold uppercase leading-none text-amber-800"
      title="Read-only kiosk session"
    >
      Kiosk
    </span>
  );
}

/**
 * slice-3690: the "New Screens" slider. Caption verbatim per Chris's note ("a slider for 'New
 * Screens'") -- no PB source, because there is no PB equivalent; this is a demo-instance view
 * preference.
 *
 * <p>A real switch, not a styled div: `<button role="switch">` carries `aria-checked`, is
 * keyboard-operable for free (Enter and Space), and takes its accessible name from the visible
 * caption, so a screen reader announces "New Screens, switch, on". Flipping it does NOT close
 * the menu -- the operator can see the knob move and change their mind.
 */
function NewScreensToggle({
  enabled,
  onChange,
}: {
  enabled: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      onClick={() => onChange(!enabled)}
      className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm text-slate-800 hover:bg-slate-100"
    >
      <span>New Screens</span>
      <span
        aria-hidden="true"
        className={
          'relative inline-flex h-4 w-8 shrink-0 items-center rounded-full transition-colors ' +
          (enabled ? 'bg-phblue-600' : 'bg-slate-300')
        }
      >
        <span
          className={
            'inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform ' +
            (enabled ? 'translate-x-4' : 'translate-x-0.5')
          }
        />
      </span>
    </button>
  );
}

/**
 * ADR-0078 (Open Decisions Q153, John 25 Oct 2026): PB's Options > "Search Exact &Match"
 * (m_phfc_main.srm:9436-9463) — a checked menu item, off by default (w_phfc_frame.srw:66-68).
 * Off: typed text criteria on every search screen match "starts with"; on: they match exactly
 * (w_phfc_search.srw:361-385). Saved per user on every click, as PB's of_set_usr('Window',
 * 'ExpertMode', 'On'|'Off'). Hidden for kiosk sessions with the other items that write.
 * Clicking does not close the menu, so the tick can be seen to change.
 */
function SearchExactMatchItem() {
  const { exact, loaded, saving, setExact } = useSearchExactMatch();
  return (
    <button
      type="button"
      role="menuitemcheckbox"
      aria-checked={exact}
      disabled={!loaded || saving}
      onClick={() => setExact(!exact)}
      className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-slate-800 hover:bg-slate-100 disabled:opacity-50"
    >
      <span aria-hidden="true" className="inline-block w-4 text-center">
        {exact ? '✓' : ''}
      </span>
      <span>Search Exact Match</span>
    </button>
  );
}

/**
 * slice-4474 (John 28 Oct) — "Auto-hide results after search (default)" On / Off: the default of
 * every screen's "Auto-hide after search" tick (slice-3671). Saved in the user's PH10 settings, so
 * it follows them to another PC. Picking a value clears every per-screen tick, so it really
 * applies everywhere; a tick changed on a screen afterwards is remembered for that screen. No PB
 * equivalent (the tick itself is a PH10 addition). Hidden for kiosk sessions with the other items
 * that write; picking does not close the menu, so the change can be seen.
 */
function AutoHideDefaultItem({ on, saving, error, onChange }: {
  on: boolean; saving: boolean; error: string | null; onChange: (on: boolean) => void;
}) {
  const seg = (value: boolean, label: string) => (
    <button
      type="button"
      role="radio"
      aria-checked={on === value}
      disabled={saving}
      onClick={() => { if (on !== value) onChange(value); }}
      className={
        'px-2 py-0.5 text-xs font-medium disabled:opacity-50 ' +
        (on === value ? 'bg-phblue-600 text-white' : 'bg-white text-slate-700 hover:bg-slate-100')
      }
    >
      {label}
    </button>
  );
  return (
    <div className="px-3 py-1.5 text-sm text-slate-800" data-testid="autohide-default">
      <div id="autohide-default-label">Auto-hide results after search (default)</div>
      <div className="mt-1 flex items-center gap-2">
        <div role="radiogroup" aria-labelledby="autohide-default-label" className="inline-flex overflow-hidden rounded border border-slate-300">
          {seg(true, 'On')}
          {seg(false, 'Off')}
        </div>
      </div>
      <p className="mt-1 text-[11px] leading-snug text-slate-500">
        Changing this resets every screen&rsquo;s own Auto-hide tick to it.
      </p>
      {error && <p className="mt-1 text-[11px] text-red-700">{error}</p>}
    </div>
  );
}

function MenuItem({
  label,
  detail,
  onClick,
  destructive,
  disabled,
  pbIcon,
}: {
  label: string;
  /** Optional muted second line -- the item's current value. */
  detail?: string;
  onClick: () => void;
  destructive?: boolean;
  disabled?: boolean;
  /** Approved PB frame icon id (docs/design/pb-icons/). Only the items that ARE a PB frame
   *  menu action carry one; the PH10-only items (Workstation, Change password, About) get
   *  no glyph rather than an invented one. */
  pbIcon?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={
        'w-full px-3 py-1.5 text-left text-sm disabled:opacity-50 ' +
        (destructive
          ? 'text-red-600 hover:bg-red-50'
          : 'text-slate-800 hover:bg-slate-100')
      }
    >
      <span className="flex items-center gap-2">
        {/* Default 20px — the handoff's own recommended range is 20-28 and the icons
            are only verified crisp from 20 up. */}
        {pbIcon && <PbIcon id={pbIcon} />}
        <span>{label}</span>
      </span>
      {detail !== undefined && (
        <span className="block truncate font-mono text-[11px] text-slate-500">{detail}</span>
      )}
    </button>
  );
}
